import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Search, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, STATUS, todayRome, vehicleLabel } from "@/lib/api";
import { StatusBadge, TicketCard } from "@/components/Ticket";

function shiftDate(ds, n) {
  const [y, m, d] = ds.split("-").map(Number);
  const dt = new Date(y, m - 1, d + n);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
}

function QueueRow({ t, onStatus, i }) {
  const live = t.status === "in_lavorazione";
  return (
    <div className={`flex gap-4 fade-up`} style={{ animationDelay: `${i * 50}ms` }} data-testid={`queue-item-${t.number}`}>
      <div className="w-16 shrink-0 text-right pt-4">
        <div className={`mono text-lg font-bold ${live ? "text-[#E50914]" : "text-white"}`}>{t.slot_time}</div>
      </div>
      <div className="relative flex flex-col items-center">
        <div className={`w-3 h-3 rounded-full mt-5 ${live ? "live-dot" : t.status === "completato" ? "bg-green-500" : "bg-[#555]"}`} />
        <div className="w-px flex-1 bg-[#383838]" />
      </div>
      <div className={`surface flex-1 p-4 mb-3 min-w-0 ${live ? "!border-[#E50914]/60" : ""}`}>
        <div className="flex flex-wrap items-center gap-2">
          <Link to={`/ticket/${t.id}`} className="font-semibold text-white hover:text-[#E50914]" data-testid={`queue-open-${t.number}`}>
            <span className="mono text-[#E50914]">#{t.number}</span> {vehicleLabel(t.vehicle)}
          </Link>
          {t.vehicle.plate && <span className="tag">{t.vehicle.plate}</span>}
          <StatusBadge status={t.status} testid={`queue-status-${t.number}`} />
        </div>
        <div className="text-sm text-slate-400 mt-1 truncate">{t.client_name} · {t.description}</div>
        <select className="field !py-1.5 !text-xs mt-3 max-w-[240px]" value={t.status} onChange={(e) => onStatus(t, e.target.value)}
          data-testid={`queue-status-select-${t.number}`}>
          {Object.entries(STATUS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
        </select>
      </div>
    </div>
  );
}

function DailyQueue() {
  const [date, setDate] = useState(todayRome());
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/queue", { params: { date } }).then(({ data }) => setData(data));
  }, [date]);
  useEffect(() => {
    load();
  }, [load]);
  const onStatus = async (t, status) => {
    try {
      await api.patch(`/tickets/${t.id}/status`, { status });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <h2 className="font-display text-2xl font-bold uppercase mr-auto">Coda Giornaliera</h2>
        <button className="btn-ghost p-2" onClick={() => setDate(shiftDate(date, -1))} data-testid="queue-prev-day"><ChevronLeft size={16} /></button>
        <input type="date" className="field !w-auto mono !py-1.5" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} data-testid="queue-date-input" />
        <button className="btn-ghost p-2" onClick={() => setDate(shiftDate(date, 1))} data-testid="queue-next-day"><ChevronRight size={16} /></button>
        <button className="btn-ghost px-3 py-2 text-xs" onClick={() => setDate(todayRome())} data-testid="queue-today">Oggi</button>
      </div>
      <div className="text-sm text-slate-400 capitalize mb-4" data-testid="queue-date-label">{fmtDate(date)}</div>
      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8" data-testid="queue-counts">
          {Object.entries(STATUS).map(([k, s]) => (
            <div key={k} className="surface p-4">
              <div className={`st ${s.cls} inline-block !whitespace-normal`}>{s.label}</div>
              <div className="mono text-3xl font-bold mt-2" data-testid={`count-${k}`}>{data.counts[k]}</div>
            </div>
          ))}
        </div>
      )}
      {data?.tickets.length === 0 && <div className="surface p-10 text-center text-slate-500" data-testid="queue-empty">Nessun appuntamento per questa data.</div>}
      <div data-testid="daily-queue-list">{data?.tickets.map((t, i) => <QueueRow key={t.id} t={t} i={i} onStatus={onStatus} />)}</div>
    </div>
  );
}

function SearchResults({ q, status }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    const h = setTimeout(() => {
      api.get("/tickets", { params: { q, status } }).then(({ data }) => setItems(data));
    }, 250);
    return () => clearTimeout(h);
  }, [q, status]);
  return (
    <div className="space-y-3" data-testid="search-results">
      <div className="text-sm text-slate-400 mono">{items ? `${items.length} risultati` : "Ricerca…"}</div>
      {items?.map((t) => <TicketCard key={t.id} t={t} showClient />)}
    </div>
  );
}

export default function AdminDashboard() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const searching = q.trim() || status;
  return (
    <div>
      <div className="eyebrow mb-2">Pannello tecnico</div>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold uppercase mb-6" data-testid="admin-dashboard-title">Coda di lavoro</h1>
      <div className="surface p-3 flex items-center gap-3 mb-3">
        <Search className="text-[#E50914] ml-2 shrink-0" size={20} />
        <input className="bg-transparent flex-1 outline-none text-base py-2 min-w-0" placeholder="Cerca per Targa, VIN, Codice Motore, Cliente o DTC…"
          value={q} onChange={(e) => setQ(e.target.value)} data-testid="global-search-input" />
        {q && <button onClick={() => setQ("")} className="text-slate-500 hover:text-white" data-testid="clear-search"><X size={18} /></button>}
      </div>
      <div className="flex flex-wrap gap-2 mb-10" data-testid="status-filters">
        {[["", "Tutti"], ...Object.entries(STATUS).map(([k, s]) => [k, s.label])].map(([k, l]) => (
          <button key={k || "all"} onClick={() => setStatus(k)} data-testid={`filter-${k || "all"}`}
            className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${status === k ? "border-[#E50914] text-white bg-[#E50914]/15" : "border-[#383838] text-slate-400 hover:text-white"}`}>
            {l}
          </button>
        ))}
      </div>
      {searching ? <SearchResults q={q.trim()} status={status} /> : <DailyQueue />}
    </div>
  );
}
